import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { ApplicationData, CampaignData } from "../../types/sponsorships/campaigns";
import { isFresh } from "../../utils/helpers";
import { fetchCampaign as fetchCampaignAPI, fetchApplication as fetchCampaignApplicationAPI } from "../../services/api/campaign";
import { ERROR, errors } from "../../constants/errors";

export interface CampaignDetailsSlice {
    loading: boolean,
    campaigns: Record<string, { campaignData: CampaignData, last_fetched_at: number, applicationData: ApplicationData }>,
    error: string | null,
    error_code: number | null
}

export const initialState: CampaignDetailsSlice = {
    loading: false,
    campaigns: {},
    error: null,
    error_code: null,
}

const STALE_TIME = apiConfigs.FETCH_CAMPAIGN.staleTime;

export const fetchCampaign = createAsyncThunk(
    'campaigns/fetchCampaign',
    async (campaignId: string, { getState, rejectWithValue }) => {
        const state = getState() as { campaignDetails: CampaignDetailsSlice };

        if (campaignId in state.campaignDetails.campaigns &&
            isFresh(state.campaignDetails.campaigns[campaignId].last_fetched_at, STALE_TIME)
        ) {
            return rejectWithValue(ERROR.CACHED)
        }

        try {
            const campaignData = await fetchCampaignAPI(campaignId);
            const applicationData = await fetchCampaignApplicationAPI(campaignId);
            return { campaignId, campaignData, applicationData };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchApplication = createAsyncThunk(
    'campaigns/fetchApplication',
    async (campaignId: string, { rejectWithValue }) => {
        try {
            const applicationData = await fetchCampaignApplicationAPI(campaignId);
            return { campaignId, applicationData };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)

const campaignDetailsSlice = createSlice({
    name: 'campaignDetails',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.error_code = null;
            })
            .addCase(fetchCampaign.fulfilled, (state, action) => {
                state.campaigns[action.payload.campaignId] = {
                    campaignData: action.payload.campaignData,
                    applicationData: action.payload.applicationData,
                    last_fetched_at: Date.now()
                }
                state.loading = false;
            })
            .addCase(fetchCampaign.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    switch (action.payload) {
                        case ERROR.NOT_FOUND:
                        default:
                            state.error_code = errors[ERROR.NOT_FOUND].code;
                            state.error = errors[ERROR.NOT_FOUND].message;
                    }
                }
            })
            .addCase(fetchApplication.fulfilled, (state, action) => {
                if (action.payload.campaignId in state.campaigns) {
                    state.campaigns[action.payload.campaignId].applicationData = action.payload.applicationData;
                }
            })
    }
})



export default campaignDetailsSlice.reducer